/* global axios */
import ApiClient from './ApiClient';

class DealsAPI extends ApiClient {
  constructor() {
    super('deals', { accountScoped: true });
  }

  /**
   * Get all deals for a pipeline
   * @param {Number} accountId - Account ID
   * @param {Number} pipelineId - Pipeline ID (optional)
   * @param {Object} params - Query parameters (pipeline_stage_id, status)
   * @returns {Promise}
   */
  get(accountId, pipelineId = null, params = {}) {
    const queryParams = { ...params };
    if (pipelineId) {
      queryParams.pipeline_id = pipelineId;
    }
    return axios.get(`/api/v1/accounts/${accountId}/deals`, { params: queryParams });
  }

  /**
   * Get a single deal
   * @param {Number} accountId - Account ID
   * @param {Number} dealId - Deal ID
   * @returns {Promise}
   */
  show(accountId, dealId) {
    return axios.get(`/api/v1/accounts/${accountId}/deals/${dealId}`);
  }

  /**
   * Create a new deal
   * @param {Number} accountId - Account ID
   * @param {Number} pipelineId - Pipeline ID
   * @param {Object} dealData - Deal data
   * @returns {Promise}
   */
  create(accountId, dealData) {
    return axios.post(`/api/v1/accounts/${accountId}/deals`, {
      deal: dealData,
    });
  }

  /**
   * Update a deal
   * @param {Number} accountId - Account ID
   * @param {Number} dealId - Deal ID
   * @param {Object} dealData - Deal data
   * @returns {Promise}
   */
  update(accountId, dealId, dealData) {
    return axios.patch(`/api/v1/accounts/${accountId}/deals/${dealId}`, dealData);
  }

  /**
   * Delete a deal
   * @param {Number} accountId - Account ID
   * @param {Number} dealId - Deal ID
   * @returns {Promise}
   */
  delete(accountId, dealId) {
    return axios.delete(`/api/v1/accounts/${accountId}/deals/${dealId}`);
  }

  /**
   * Move a deal to another stage
   * @param {Number} accountId - Account ID
   * @param {Number} dealId - Deal ID
   * @param {Number} targetStageId - Target pipeline stage ID
   * @param {Number} targetPosition - Target position in the stage (optional)
   * @returns {Promise}
   */
  move(accountId, dealId, targetStageId) {
    return axios.patch(
      `/api/v1/accounts/${accountId}/deals/${dealId}/move`,
      { pipeline_stage_id: targetStageId }
    );
  }

  /**
   * Filter deals by status
   * @param {Number} accountId - Account ID
   * @param {String} status - Status (open, won, lost)
   * @returns {Promise}
   */
  filterByStatus(accountId, status) {
    return this.get(accountId, null, { status });
  }

  /**
   * Filter deals by stage
   * @param {Number} accountId - Account ID
   * @param {Number} pipelineStageId - Pipeline stage ID
   * @returns {Promise}
   */
  filterByStage(accountId, pipelineStageId) {
    return this.get(accountId, null, { pipeline_stage_id: pipelineStageId });
  }
}

export default new DealsAPI();